import {
  EventNames,
  type GlobalEventRegistry,
  globalEventBus,
} from '@littlebird/event-bus';
import {
  type ChatMessageBatchChunk,
  type ChatMessageBatchPayload,
  type IWebSocketManager,
  type LittlebirdSDK,
  WSMethods,
  handleChatMessage,
  handleChatMessageBatch,
  handleServerError,
  parseActionCardsChangedPayload,
} from '@littlebird/sdk';

import {
  WebSocketAPI,
  type WebSocketConnectedPayload,
  type WebSocketMessagePayload,
} from '@/features/electron-api/features/WebSocketAPI';
import { subscriptionApis } from '@/features/subscription/subscription.query';

type ConnectionState = 'disconnected' | 'connecting' | 'connected';

const CONNECTION_TIMEOUT_MS = 15_000;

export class IPCWebSocketAdapter implements IWebSocketManager {
  private sdk: LittlebirdSDK | null = null;
  private wsApi: WebSocketAPI;
  private cleanupFunctions: (() => void)[] = [];

  private _connectionState: ConnectionState = 'disconnected';
  private _connectionError: string | null = null;
  public lastKnownConnectedAt = new Date(0);

  private connectionResolvers: {
    resolve: (connected: boolean) => void;
    timeoutId: ReturnType<typeof setTimeout> | null;
  }[] = [];
  private ensureConnectionPromise: Promise<boolean> | null = null;

  constructor(wsApi?: WebSocketAPI) {
    this.wsApi = wsApi ?? new WebSocketAPI();
  }

  setSDK(sdk: LittlebirdSDK): void {
    // Only the adapter an SDK holds as `wsManager` may register handlers for
    // it. Wiring any other pair would add a second set of live IPC
    // subscriptions, and each set delivers chat events to the stores again.
    if (sdk.wsManager !== this) {
      sdk.logger.warn(
        "IPCWebSocketAdapter: setSDK ignored, adapter is not this SDK's wsManager"
      );
      return;
    }
    this.sdk = sdk;
    this.sdk.logger.info('IPCWebSocketAdapter: SDK set, registering handlers');
    this.setupMessageHandlers();
  }

  async connect(): Promise<void> {
    if (!this.sdk) {
      throw new Error('SDK not set. Call setSDK() first.');
    }

    if (this._connectionState === 'connected') {
      this.sdk.logger.info(
        'IPCWebSocketAdapter: connect() skipped, already connected'
      );
      return;
    }

    if (this._connectionState === 'connecting') {
      this.sdk.logger.info(
        'IPCWebSocketAdapter: connect() waiting for existing connection',
        {
          pendingResolvers: this.connectionResolvers.length,
        }
      );

      const connected = await this.waitForConnection(CONNECTION_TIMEOUT_MS);
      if (!connected) {
        throw new Error(
          this._connectionError || 'WebSocket connection timeout'
        );
      }
      return;
    }

    const connectStartedAt = Date.now();
    this.sdk.logger.info('IPCWebSocketAdapter: connect() called', {
      currentState: this._connectionState,
      pendingResolvers: this.connectionResolvers.length,
    });

    this._connectionError = null;
    this._connectionState = 'connecting';

    // Avoid rebinding on every connect — tears down availability listener mid-flight
    if (this.cleanupFunctions.length === 0) {
      this.setupMessageHandlers();
    }

    const result = await this.wsApi.connect();

    this.sdk.logger.info('IPCWebSocketAdapter: connect result', {
      result,
      durationMs: Date.now() - connectStartedAt,
    });

    if (!result.success) {
      this._connectionState = 'disconnected';
      this._connectionError = result.error || 'Failed to connect via IPC';
      this.sdk.logger.error('WebSocket IPC connect failed', {
        error: result.error,
      });
      throw new Error(this._connectionError);
    }

    if (result.state === 'ready') {
      this._connectionState = 'connected';
      this.lastKnownConnectedAt = new Date();
      this._connectionError = null;
      // Ready short-circuit does not emit availability-changed on its own
      this.sdk.transport.setWsAvailability(true);
      this.resolveAllConnectionResolvers(true);
      this.sdk.logger.info('IPCWebSocketAdapter: WebSocket already ready');
      return;
    }

    // State is 'connecting' - wait for ws:connected event
    const waitStartedAt = Date.now();
    this.sdk.logger.info('IPCWebSocketAdapter: waiting for ws:connected', {
      timeoutMs: CONNECTION_TIMEOUT_MS,
    });

    const connected = await this.waitForConnection(CONNECTION_TIMEOUT_MS);
    this.sdk.logger.info('IPCWebSocketAdapter: waitForConnection finished', {
      connected,
      durationMs: Date.now() - waitStartedAt,
      connectionState: this._connectionState,
      connectionError: this._connectionError,
    });

    if (!connected) {
      const errorMsg = this._connectionError || 'WebSocket connection timeout';
      this._connectionState = 'disconnected';
      throw new Error(errorMsg);
    }
    this._connectionState = 'connected';
    this.sdk.logger.info('IPCWebSocketAdapter: WebSocket ready');
  }

  disconnect(): void {
    this.sdk?.logger.info('IPCWebSocketAdapter: disconnect() called', {
      currentState: this._connectionState,
      cleanupFunctions: this.cleanupFunctions.length,
      pendingResolvers: this.connectionResolvers.length,
    });

    this.ensureConnectionPromise = null;
    this.resolveAllConnectionResolvers(false);
    this._connectionError = null;

    this.cleanupFunctions.forEach((cleanup) => cleanup());
    this.cleanupFunctions = [];

    // Use disconnect() instead of forceDisconnect() to preserve multi-window sharing.
    // This decrements the renderer count; the main process will only close the
    // WebSocket when all renderers have disconnected.
    this.wsApi.disconnect().catch((err) => {
      this.sdk?.logger.error('WebSocket IPC disconnect failed', { error: err });
    });

    this._connectionState = 'disconnected';
    this.lastKnownConnectedAt = new Date(0);
  }

  isConnected(): boolean {
    return this._connectionState === 'connected';
  }

  async ensureConnection(timeoutMs = CONNECTION_TIMEOUT_MS): Promise<boolean> {
    if (this.isConnected()) {
      return true;
    }

    if (this.ensureConnectionPromise) {
      return this.ensureConnectionPromise;
    }

    this.ensureConnectionPromise = this.doEnsureConnection(timeoutMs).finally(
      () => {
        this.ensureConnectionPromise = null;
      }
    );

    return this.ensureConnectionPromise;
  }

  getConnectionState(): ConnectionState {
    return this._connectionState;
  }

  getConnectionError(): string | null {
    return this._connectionError;
  }

  async resetIfStale(): Promise<void> {
    this.sdk?.logger.info(
      'IPCWebSocketAdapter: resetIfStale delegating to main process'
    );
    try {
      await this.wsApi.resetIfStale();
    } catch (err) {
      this.sdk?.logger.error('IPCWebSocketAdapter: resetIfStale failed', {
        error: err,
      });
    }
  }

  async waitForConnection(timeoutMs?: number): Promise<boolean> {
    if (this._connectionState === 'connected') return true;

    if (this._connectionState === 'disconnected') {
      this.sdk?.logger.info(
        'IPCWebSocketAdapter: waitForConnection initiating connection'
      );
      try {
        await (timeoutMs
          ? Promise.race([
              this.connect(),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('timeout')), timeoutMs)
              ),
            ])
          : this.connect());
        return this.isConnected();
      } catch {
        return false;
      }
    }

    return new Promise<boolean>((resolve) => {
      const timeoutId = timeoutMs
        ? setTimeout(() => {
            const idx = this.connectionResolvers.findIndex(
              (r) => r.resolve === resolve
            );
            if (idx !== -1) {
              this.connectionResolvers.splice(idx, 1);
            }
            resolve(false);
          }, timeoutMs)
        : null;

      this.connectionResolvers.push({ resolve, timeoutId });
    });
  }

  private async doEnsureConnection(timeoutMs: number): Promise<boolean> {
    const connected = await this.waitForConnection(timeoutMs);
    if (connected || this.isConnected()) {
      return true;
    }

    this.sdk?.logger.warn(
      'IPCWebSocketAdapter: ensureConnection forcing reconnect',
      {
        connectionState: this._connectionState,
      }
    );

    return this.forceReconnect(timeoutMs);
  }

  private async forceReconnect(timeoutMs: number): Promise<boolean> {
    this._connectionError = null;
    this._connectionState = 'connecting';

    try {
      const result = await this.wsApi.reconnect();

      if (!result.success) {
        this._connectionState = 'disconnected';
        this._connectionError = result.error || 'WebSocket reconnect failed';
        return false;
      }

      if (result.state === 'ready') {
        this._connectionState = 'connected';
        this.lastKnownConnectedAt = new Date();
        this.sdk?.transport.setWsAvailability(true);
        return true;
      }

      return this.waitForConnection(timeoutMs);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'WebSocket reconnect failed';
      this._connectionState = 'disconnected';
      this._connectionError = message;
      this.sdk?.logger.error('IPCWebSocketAdapter: reconnect failed', {
        error,
      });
      return false;
    }
  }

  private resolveAllConnectionResolvers(connected: boolean): void {
    const count = this.connectionResolvers.length;
    if (count > 0) {
      this.sdk?.logger.info(
        'IPCWebSocketAdapter: Resolving connection waiters',
        {
          connected,
          count,
        }
      );
    }
    for (const resolver of this.connectionResolvers) {
      if (resolver.timeoutId) {
        clearTimeout(resolver.timeoutId);
      }
      resolver.resolve(connected);
    }
    this.connectionResolvers = [];
  }

  private setupMessageHandlers(): void {
    if (!this.sdk) return;

    if (this.cleanupFunctions.length > 0) {
      this.sdk.logger.debug(
        'IPCWebSocketAdapter: Cleaning up previous handlers',
        {
          count: this.cleanupFunctions.length,
        }
      );
    }
    this.cleanupFunctions.forEach((cleanup) => cleanup());
    this.cleanupFunctions = [];

    const cleanupMessage = this.wsApi.onMessage((payload) => {
      this.lastKnownConnectedAt = new Date();
      this.handleMessage(payload);
    });
    this.cleanupFunctions.push(cleanupMessage);

    const cleanupConnected = this.wsApi.onConnected(
      ({ reconnectReason }: WebSocketConnectedPayload = {}) => {
        this.sdk?.logger.info('IPCWebSocketAdapter: Connected');
        this._connectionState = 'connected';
        this.lastKnownConnectedAt = new Date();
        this._connectionError = null;
        this.sdk?.transport.setWsAvailability(true);

        this.resolveAllConnectionResolvers(true);
        // Emit only for reconnects because `ThreadStore.handleWSReconnect` consumes this event
        if (reconnectReason) {
          globalEventBus.emit(EventNames.WS_RECONNECTED, {
            data: { reason: reconnectReason },
          });
        }
      }
    );
    this.cleanupFunctions.push(cleanupConnected);

    const cleanupDisconnected = this.wsApi.onDisconnected(async (payload) => {
      this.sdk?.logger.info('IPCWebSocketAdapter: Disconnected', {
        reason: payload.reason,
        code: payload.code,
      });
      this._connectionState = 'disconnected';

      if (payload.reason) {
        this._connectionError = payload.reason;
      } else {
        this._connectionError = `Connection closed (code: ${payload.code})`;
      }

      this.resolveAllConnectionResolvers(false);

      try {
        const diagnostics = await this.wsApi.getDiagnostics();
        this.sdk?.logger.info('IPCWebSocketAdapter: Diagnostics', {
          diagnostics,
        });
      } catch {
        // ignore diagnostics fetch errors
      }
    });
    this.cleanupFunctions.push(cleanupDisconnected);

    const cleanupError = this.wsApi.onError(async (payload) => {
      this.sdk?.logger.error('IPCWebSocketAdapter: Error', {
        error: payload.error,
      });

      this._connectionError = payload.error;

      try {
        const diagnostics = await this.wsApi.getDiagnostics();
        this.sdk?.logger.info('IPCWebSocketAdapter: Diagnostics', {
          diagnostics,
        });
      } catch {
        // ignore diagnostics fetch errors
      }
    });
    this.cleanupFunctions.push(cleanupError);

    const cleanupAvailability = this.wsApi.onAvailabilityChanged((payload) => {
      this.sdk?.logger.info('IPCWebSocketAdapter: WS availability changed', {
        available: payload.available,
        errorMessage: payload.errorMessage,
      });

      this.sdk?.transport.setWsAvailability(
        payload.available,
        payload.errorMessage
      );
    });
    this.cleanupFunctions.push(cleanupAvailability);
  }

  private handleMessage(payload: WebSocketMessagePayload): void {
    if (!this.sdk) return;

    this.sdk.logger.debug('IPCWebSocketAdapter: Message received', {
      payloadType: payload.type,
    });

    switch (payload.type) {
      case WSMethods.CHAT_MESSAGE:
        handleChatMessage(
          payload.data as ChatMessageBatchChunk,
          this.sdk.stores,
          this.sdk.transport,
          this.sdk.logger
        );
        break;
      case WSMethods.CHAT_MESSAGE_BATCH:
        handleChatMessageBatch(
          payload.data as ChatMessageBatchPayload,
          this.sdk.stores,
          this.sdk.transport,
          this.sdk.logger
        );
        break;
      case WSMethods.ERROR: {
        const { shouldReconnect } = handleServerError(
          payload.error,
          this.sdk.logger
        );
        if (shouldReconnect) {
          this.sdk.logger.info(
            'IPCWebSocketAdapter: Token error detected, main process will reconnect'
          );
        }
        break;
      }
      case 'push_notification':
        globalEventBus.emit(EventNames.PUSH_NOTIFICATION, {
          data: payload.data as {
            title: string;
            body: string;
            data?: Record<string, string> | null;
          },
        });
        break;
      case WSMethods.ROUTINES_UPDATED:
        globalEventBus.emit(EventNames.ROUTINES_UPDATED, {
          data: payload.data as GlobalEventRegistry[typeof EventNames.ROUTINES_UPDATED],
        });
        break;
      case WSMethods.BILLING_UPDATED:
        globalEventBus.emit(EventNames.BILLING_UPDATED, { data: {} });
        break;
      case WSMethods.ACTION_CARDS_CHANGED:
        globalEventBus.emit(EventNames.ACTION_CARDS_CHANGED, {
          data: parseActionCardsChangedPayload(payload.data),
        });
        break;
      // Each of these push types has the name of the event it emits
      case WSMethods.PROJECTS_CHANGED:
      case WSMethods.CHATS_CHANGED:
      case WSMethods.TEAM_CHATS_CHANGED:
        globalEventBus.emit(payload.type as keyof GlobalEventRegistry, {
          data: payload.data as GlobalEventRegistry[keyof GlobalEventRegistry],
        });
        break;
      case 'team_policy_changed':
        void subscriptionApis.getStatus();
        break;
      case WSMethods.PEOPLE_WIKI_CHANGED:
        globalEventBus.emit(EventNames.PEOPLE_WIKI_CHANGED, {
          data: payload.data as GlobalEventRegistry[typeof EventNames.PEOPLE_WIKI_CHANGED],
        });
        break;
      case WSMethods.MEETING_INTENT_DETECTED:
        globalEventBus.emit(EventNames.MEETING_INTENT_DETECTED, {
          data: payload.data as GlobalEventRegistry[typeof EventNames.MEETING_INTENT_DETECTED],
        });
        break;
      default:
        this.sdk.logger.warn('IPCWebSocketAdapter: Unknown message type', {
          payloadType: payload.type,
        });
    }
  }

  // for testing
  destroy(): void {
    this.sdk?.logger.info('IPCWebSocketAdapter: destroy() called');
    this.disconnect();
    this.wsApi.destroy();
    this.sdk = null;
  }
}
